import { useRouter } from 'next/router';
import React, { memo, useCallback } from 'react';

import { Link } from '@/core/routing/components/Link';
import { isB2C } from '@/core/settings/utils';
import { useCategoryHubBreadcrumb } from '@/productDiscovery/CategoryHub/hooks/categoryHubBreadcrumb';
import { leonidasLinkAdvice } from '@/productDiscovery/HeaderLeonidas/b2c/modules/translations';
import type { MenuCategory } from '@/productDiscovery/HeaderLeonidas/interfaces/MenuTree.model';
import { extractFamilyID } from '@/productDiscovery/HeaderLeonidas/utils/extractFamilyID';
import { useCategoryMetadata } from '@/productDiscovery/Listings/pages/Category/hooks/category';
import { useMassLandingMetadata } from '@/productDiscovery/Listings/pages/MassLanding/hooks/massLanding';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';
import { footerSitemapAdviceHref } from '@/retention/Footer/translations';

export type MenuForSEOProps = {
  categories?: MenuCategory[];
  innerHtml: string;
};

const Menu = ({ categories, innerHtml }: MenuForSEOProps) => {
  /* Menu DOM Siloing */
  const router = useRouter();
  const productMetadata = useDetail();
  const categoryHubBreadcrumb = useCategoryHubBreadcrumb();
  const categoryMetadata = useCategoryMetadata();
  const massLandingMetadata = useMassLandingMetadata();

  const isFamilyExpandedInDOM = useCallback(
    (family: MenuCategory) => {
      if (router.route === '/homepage-b2c') {
        return family.children && family.children.length > 0;
      }

      if (router.route === '/product') {
        return productMetadata?.breadcrumbs?.length > 0
          ? productMetadata?.breadcrumbs?.[0].id === Number(family.id)
          : false;
      }

      if (router.route === '/category-hub') {
        return categoryHubBreadcrumb && categoryHubBreadcrumb?.length > 0
          ? categoryHubBreadcrumb?.[1].id === Number(family.id)
          : false;
      }

      if (router.route === '/category') {
        return categoryMetadata?.breadcrumb &&
          categoryMetadata?.breadcrumb?.length > 0
          ? categoryMetadata?.breadcrumb?.[1].id === Number(family.id)
          : false;
      }

      if (router.route === '/mass-landing') {
        if (
          massLandingMetadata?.breadcrumb &&
          massLandingMetadata?.breadcrumb?.length > 0
        ) {
          const familyID = extractFamilyID(
            massLandingMetadata?.breadcrumb?.[1].href,
          );
          return Number(familyID) === Number(family.id);
        }
        return false;
      }

      return false;
    },
    [
      router.route,
      productMetadata?.breadcrumbs,
      categoryHubBreadcrumb,
      categoryMetadata?.breadcrumb,
      massLandingMetadata?.breadcrumb,
    ],
  );

  if (innerHtml) {
    return (
      <nav
        id="menu-SEO"
        className="hidden h-0 w-0"
        data-version={
          typeof window !== 'undefined' ? 'client-with-inner-html' : 'server'
        }
        suppressHydrationWarning
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: innerHtml }}
      />
    );
  }

  return (
    <nav id="menu-SEO" className="hidden h-0 w-0">
      <ul>
        {isB2C() && (
          <li aria-hidden>
            <Link href={footerSitemapAdviceHref}>{leonidasLinkAdvice}</Link>
          </li>
        )}
        {categories?.map((family) => (
          <li key={family.id} aria-hidden>
            <Link href={family.url}>{family.name}</Link>
            {isFamilyExpandedInDOM(family) && family.children?.length ? (
              <ul>
                {family.children?.map((category) => (
                  <li key={category.id} aria-hidden>
                    <Link href={category.url}>{category.name}</Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export const MenuForSEO = memo(Menu, (prevProps) => prevProps.innerHtml !== '');
